// Reusable time picker initializer: Flatpickr + masked numeric input
// Usage: Add data-time-picker to wrapper, include a visible input with data-hidden-input sibling hidden field

function formatDigitsToTime(d) {
  d = (d || '').replace(/\D/g, '').slice(0, 4);
  if (d.length <= 2) return d;
  return d.slice(0, 2) + ':' + d.slice(2);
}

function clampTime(h, m) {
  const hh = Math.min(Math.max(parseInt(h || '0', 10), 0), 23);
  const mm = Math.min(Math.max(parseInt(m || '0', 10), 0), 59);
  return String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
}

function extractHHMM(value) {
  if (!value) return '';
  const s = String(value);
  // If already HH:MM
  let m = s.match(/^(\d{2}):(\d{2})$/);
  if (m) return m[0];
  // If HH:MM:SS
  m = s.match(/^(\d{2}):(\d{2}):(\d{2})$/);
  if (m) return `${m[1]}:${m[2]}`;
  // If contains a time segment in a longer string (e.g., full timestamp)
  m = s.match(/(\d{2}):(\d{2})(?::\d{2})?/);
  if (m) return `${m[1]}:${m[2]}`;
  return '';
}

function parseSelectorList(list) {
  if (!list) return [];
  try {
    return list
      .split(',')
      .map(s => s.trim())
      .map(s => s.replace(/^['"]+|['"]+$/g, ''))
      .map(s => s.replace(/[\n\r]/g, ''))
      .filter(Boolean);
  } catch (_) {
    return [];
  }
}

function anySelectorsHaveValue(selectors) {
  if (!selectors || selectors.length === 0) return false;
  for (let sel of selectors) {
    sel = sel.replace(/^['"]+|['"]+$/g, '');
    const nodes = document.querySelectorAll(sel);
    for (const el of nodes) {
      const val = (el && 'value' in el) ? String(el.value || '').trim() : '';
      if (val !== '') return true;
    }
  }
  return false;
}

function allSelectorsHaveValue(selectors) {
  if (!selectors || selectors.length === 0) return true;
  for (let sel of selectors) {
    sel = sel.replace(/^['"]+|['"]+$/g, '');
    const nodes = document.querySelectorAll(sel);
    if (nodes.length === 0) return false;
    let anyNonEmpty = false;
    for (const el of nodes) {
      const val = (el && 'value' in el) ? String(el.value || '').trim() : '';
      if (val !== '') { anyNonEmpty = true; break; }
    }
    if (!anyNonEmpty) return false;
  }
  return true;
}

function clearBySelectors(selectors) {
  if (!selectors) return;
  for (const sel of selectors) {
    const nodes = document.querySelectorAll(sel);
    for (const el of nodes) {
      if ('value' in el) el.value = '';
      try { el.dispatchEvent(new Event('input', { bubbles: true })); } catch(_) {}
      try { el.dispatchEvent(new Event('change', { bubbles: true })); } catch(_) {}
    }
  }
}

function disableBySelectors(selectors) {
  if (!selectors) return;
  for (const sel of selectors) {
    const nodes = document.querySelectorAll(sel);
    for (const el of nodes) {
      try { el.disabled = true; } catch(_) {}
    }
  }
}

function clearErrorsBySelectors(selectors) {
  if (!selectors) return;
  for (const sel of selectors) {
    const nodes = document.querySelectorAll(sel);
    for (const el of nodes) {
      const id = el && el.id ? el.id : null;
      if (id) {
        try { el.classList.remove('is-invalid'); } catch(_) {}
        try {
          const group = el.closest('.seamless-group') || el.closest('.range-group');
          if (group) {
            group.classList.remove('invalid');
            group.classList.remove('is-invalid');
          }
        } catch(_) {}
        try {
          const fb = document.getElementById(id + '_feedback');
          if (fb) { fb.style.display = 'none'; fb.textContent = ''; }
        } catch(_) {}
      }
    }
  }
}

// Check if new event time would cause validation conflicts with existing registration dates
// Returns an object with conflict information: { hasConflict: boolean, conflictType: string, conflictField: string }
function wouldTimeChangeCauseValidationConflict(inputId, newTimeValue) {
  const regStartDate = document.querySelector('#registration_start_date')?.value;
  const regEndDate = document.querySelector('#registration_end_date')?.value;
  const regStartTime = document.querySelector('#registration_start_time')?.value;
  const regEndTime = document.querySelector('#registration_end_time')?.value;
  const eventStartDate = document.querySelector('#event_start_date')?.value;
  const eventEndDate = document.querySelector('#event_end_date')?.value;
  const eventStartTime = document.querySelector('#event_start_time')?.value;
  const eventEndTime = document.querySelector('#event_end_time')?.value;
  const publishAt = document.querySelector('#publish_at_hidden')?.value;

  // If no registration dates are set, no conflict
  if (!regStartDate && !regEndDate && !publishAt) {
    return { hasConflict: false, conflictType: '', conflictField: '' };
  }

  // Determine which time is being changed and create the new event time values
  let newEventStartTime = eventStartTime;
  let newEventEndTime = eventEndTime;
  
  if (inputId === 'event_start_time') {
    newEventStartTime = newTimeValue;
  } else if (inputId === 'event_end_time') {
    newEventEndTime = newTimeValue;
  }

  // Rule: Event start time must be after registration end time
  if (regEndDate && regEndTime && eventStartDate && newEventStartTime) {
    const regEndDateTime = new Date(regEndDate + 'T' + regEndTime);
    const eventStartDateTime = new Date(eventStartDate + 'T' + newEventStartTime);
    
    if (eventStartDate === regEndDate) {
      // When dates are equal, event start time must be after registration end time
      if (eventStartDateTime <= regEndDateTime) {
        return { hasConflict: true, conflictType: 'time_before_registration_end', conflictField: 'Registration End Time' };
      }
    } else if (eventStartDateTime <= regEndDateTime) {
      return { hasConflict: true, conflictType: 'time_before_registration_end', conflictField: 'Registration End Time' };
    }
  }

  // Rule: Event end time must be after event start time (internal consistency)
  // Only check this when changing event_end_time, not when changing event_start_time
  if (inputId === 'event_end_time' && eventStartDate && eventEndDate && newEventStartTime && newEventEndTime) {
    const eventStartDateTime = new Date(eventStartDate + 'T' + newEventStartTime);
    const eventEndDateTime = new Date(eventEndDate + 'T' + newEventEndTime);
    
    if (eventStartDate === eventEndDate && eventEndDateTime <= eventStartDateTime) {
      return { hasConflict: true, conflictType: 'end_before_start', conflictField: 'Event Start Time' };
    }
  }

  // Rule: Event start date/time must be on or after registration start date
  // This ensures events don't start before registration period begins
  if (regStartDate && eventStartDate && newEventStartTime) {
    if (eventStartDate < regStartDate) {
      return { hasConflict: true, conflictType: 'date_before_registration_start', conflictField: 'Registration Start Date' };
    }
    // If dates are equal, check time constraints with registration start time
    if (regStartTime && eventStartDate === regStartDate) {
      const eventStartDateTime = new Date(eventStartDate + 'T' + newEventStartTime);
      const regStartDateTime = new Date(regStartDate + 'T' + regStartTime);
      if (eventStartDateTime < regStartDateTime) {
        return { hasConflict: true, conflictType: 'time_before_registration_start', conflictField: 'Registration Start Time' };
      }
    }
  }

  // Rule: Event end date/time must be on or after registration start date
  // This ensures events don't end before registration period begins
  if (regStartDate && eventEndDate && newEventEndTime) {
    if (eventEndDate < regStartDate) {
      return { hasConflict: true, conflictType: 'date_before_registration_start', conflictField: 'Registration Start Date' };
    }
  }

  // Rule: Send Invitation At must be before Registration Start Time
  if (publishAt && regStartDate && regStartTime) {
    const publishAtDate = new Date(publishAt);
    const regStartDateTime = new Date(regStartDate + 'T' + regStartTime);
    if (publishAtDate > regStartDateTime) {
      return { hasConflict: true, conflictType: 'publish_after_registration', conflictField: 'Registration Start Time' };
    }
  }

  return { hasConflict: false, conflictType: '', conflictField: '' };
}

// Use message exactly as provided via data-confirm-message

function initOne(el) {
  if (el.dataset.tpInitialized) return;
  el.dataset.tpInitialized = '1';
  const input = el.querySelector('input.form-control');
  const hidden = el.parentElement.querySelector('input[type="hidden"][name$="_time]"]') || el.nextElementSibling;
  if (!input || !hidden) return;

  // Prefill visible from hidden if present
  if (hidden.value && !input.value) {
    const hhmm = extractHHMM(hidden.value);
    if (hhmm) {
      input.value = hhmm;
      hidden.value = hhmm;
    }
  }

  // Flatpickr init if available
  if (typeof flatpickr !== 'undefined') {
    // Confirmation settings (opt-in via data attributes on wrapper)
    let confirmEnabled = String(el.dataset.confirmClearOnChange || '').toLowerCase() === 'true';
    let msg = el.dataset.confirmMessage || '';
    let clearTargets = parseSelectorList(el.dataset.clearTargets || '');
    let disableTargets = parseSelectorList(el.dataset.disableTargets || '');
    let checkSelectors = parseSelectorList(el.dataset.checkSelectors || el.dataset.clearTargets || '');
    let requireSelectors = parseSelectorList(el.dataset.requireSelectors || '');

    // Fallback for Invitations page if data attrs missing
    const inputId = input.id || '';
    if (!confirmEnabled && (inputId === 'event_start_time' || inputId === 'event_end_time')) {
      confirmEnabled = true;
      if (checkSelectors.length === 0) {
        checkSelectors = parseSelectorList('#registration_start_datetime,#registration_end_datetime,#publish_at_display,#registration_start_date,#registration_start_time,#registration_end_date,#registration_end_time,#publish_at_hidden');
      }
      if (requireSelectors.length === 0) {
        requireSelectors = parseSelectorList('#event_start_date,#event_end_date,#event_start_time,#event_end_time');
      }
      if (!msg) msg = (inputId === 'event_start_time')
        ? 'Changing the Event Start Time will clear Registration Start Time, Registration End Time, and Send Invitation At. Do you want to proceed?'
        : 'Changing the Event End Time will clear Registration Start Time, Registration End Time, and Send Invitation At. Do you want to proceed?';
    }

    let prevVis = input.value;
    let prevHidden = hidden.value;

    // Clear button plugin configuration
    const clearButtonPlugin = function(config) {
      return function(fp) {
        return {
          onReady() {
            const clearBtn = document.createElement('button');
            clearBtn.type = 'button';
            clearBtn.className = 'btn btn-sm btn-outline-secondary flatpickr-clear-btn';
            clearBtn.innerHTML = config.html || 'Clear';
            clearBtn.style.cssText = 'margin-left: 8px;';
            
            // Add clear button next to calendar container
            clearBtn.addEventListener('click', function(e) {
              e.preventDefault();
              fp.clear();
              // Clear hidden field
              hidden.value = '';
              // Clear validation errors
              input.classList.remove('is-invalid');
              const group = input.closest('.seamless-group') || input.closest('.range-group');
              if (group) group.classList.remove('invalid', 'is-invalid');
              const id = input.id;
              if (id) {
                const fb = document.getElementById(id + '_feedback');
                if (fb) { fb.style.display = 'none'; fb.textContent = ''; }
              }
              // Update previous values
              prevVis = '';
              prevHidden = '';
              // Trigger change event
              input.dispatchEvent(new Event('change', { bubbles: true }));
            });
            
            // Append to calendar container
            if (fp.calendarContainer) {
              fp.calendarContainer.appendChild(clearBtn);
            }
          }
        };
      };
    };

    flatpickr(input, {
      enableTime: true,
      noCalendar: true,
      dateFormat: 'H:i',
      time_24hr: true,
      allowInput: true,
      defaultDate: input.value || null,
      plugins: [
        clearButtonPlugin({
          html: '<i class="fas fa-times-circle"></i> Clear'
        })
      ],
      onOpen: function() {
        prevVis = input.value;
        prevHidden = hidden.value;
      },
      onChange: function (selectedDates, dateStr) {
        // Keep hidden value in sync while the picker is open; conflict checks run onClose.
        hidden.value = dateStr;
      },
      onClose: function() {
        const finalValue = input.value;
        const changed = (finalValue !== prevHidden);
        hidden.value = finalValue;

        if (confirmEnabled) {
          const hasDependents = anySelectorsHaveValue(checkSelectors);
          const eventReady = allSelectorsHaveValue(requireSelectors);
          const conflictInfo = wouldTimeChangeCauseValidationConflict(inputId, finalValue);
          
          if (changed && hasDependents && eventReady && conflictInfo.hasConflict) {
            // Generate specific conflict message
            let conflictMessage = '';
            const fieldName = inputId === 'event_start_time' ? 'Event Start Time' : 'Event End Time';
            
            if (conflictInfo.conflictType === 'time_before_registration_end') {
              conflictMessage = `The ${fieldName.toLowerCase()} you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Event start time must be after registration end time. Do you want to proceed?`;
            } else if (conflictInfo.conflictType === 'time_before_registration_start') {
              conflictMessage = `The ${fieldName.toLowerCase()} you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Event start time must be on or after registration start time. Do you want to proceed?`;
            } else if (conflictInfo.conflictType === 'date_before_registration_start') {
              conflictMessage = `The ${fieldName.toLowerCase()} you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Event must be on or after registration start date. Do you want to proceed?`;
            } else if (conflictInfo.conflictType === 'end_before_start') {
              conflictMessage = `The ${fieldName.toLowerCase()} you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Event end time must be after event start time. Do you want to proceed?`;
            } else if (conflictInfo.conflictType === 'publish_after_registration') {
              conflictMessage = `The ${fieldName.toLowerCase()} you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Send Invitation At must be before Registration Start Time. Do you want to proceed?`;
            } else {
              // Fallback to default message if conflict type is not recognized
              conflictMessage = (msg && String(msg)) || '';
            }
            
            const text = conflictMessage || (msg && String(msg)) || '';
            const proceed = window.confirm(text);
            if (!proceed) {
              // Restore previous
              input.value = prevVis;
              hidden.value = prevHidden;
              input.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }

            // Only clear dependent fields when the conflict involves registration/publish timings.
            const shouldClearDependents = conflictInfo.conflictType !== 'end_before_start';
            if (shouldClearDependents) {
              // Default: clear all configured dependents
              let targetsToClear = clearTargets;
              let targetsToDisable = disableTargets;

              // When the conflict is strictly with Registration End, avoid clearing Registration Start.
              if (conflictInfo.conflictType === 'time_before_registration_end') {
                targetsToClear = clearTargets.filter(sel =>
                  sel.includes('registration_end_datetime') ||
                  sel.includes('registration_end_date') ||
                  sel.includes('registration_end_time')
                );
                targetsToDisable = disableTargets.filter(sel =>
                  sel.includes('registration_end_datetime')
                );
              }

              clearBySelectors(targetsToClear);
              disableBySelectors(targetsToDisable);
              clearErrorsBySelectors(targetsToClear);
            }
          }
        }

        // Trigger validations bound to change after final value is settled.
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // Masked typing
  input.addEventListener('input', function () {
    const formatted = formatDigitsToTime(input.value);
    input.value = formatted;
    if (/^\d{2}:\d{2}$/.test(formatted)) {
      hidden.value = formatted;
    } else {
      hidden.value = '';
    }
  });

  input.addEventListener('blur', function () {
    const digits = (input.value || '').replace(/\D/g, '');
    if (digits.length === 4) {
      const clamped = clampTime(digits.slice(0, 2), digits.slice(2, 4));
      input.value = clamped;
      hidden.value = clamped;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  input.addEventListener('keydown', function (e) {
    const k = e.key;
    let allow = false;
    if (k === 'Backspace' || k === 'Delete' || k === 'Tab' || k === 'Enter' ||
      k === 'ArrowLeft' || k === 'ArrowRight' || k === 'Home' || k === 'End') allow = true;
    if (/^\d$/.test(k)) allow = true;
    if ((e.ctrlKey || e.metaKey) && (k === 'a' || k === 'c' || k === 'v' || k === 'x')) allow = true;
    if (!allow) e.preventDefault();
  });
}

export function initAllTimePickers() {
  document.querySelectorAll('[data-time-picker]').forEach(initOne);
}

// Auto-init on Turbolinks and DOM ready
document.addEventListener('turbolinks:load', initAllTimePickers);
document.addEventListener('DOMContentLoaded', initAllTimePickers);
